import { Button, Col, Form, FormInstance, Input, InputNumber, Modal, Row, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import { Emission, SettingChemicalProp } from "src/entity/settings/terminal-market";
import { generateErrorMessage } from "src/utils/error-message";
import { Option } from "src/entity/settings/masterdata";
import { onKeyDownNumberOnlyAndCopyPaste, onKeyDownNumberWithFixedDecimal } from "src/utils/key-down-validate";
import {
  cleanNumberStringInObject,
  formatNumber,
  formatNumberWithFillZero,
  formatOnlyComma,
  parseAndFormatScientificNumber,
  parseAndFormatScientificNumberWithoutZeroReturn,
  ParserNumber,
} from "src/utils/process-number";
import { generateInputNumberProps } from "src/utils/generate";
import MoneyInput from "src/components/MoneyInput/MoneyInput";
import { debounce } from "lodash";

const { Text } = Typography;

const ChemicalPropertyModal = (props: {
  form: FormInstance;
  open: boolean;
  mode: "insert" | "update";
  loading: boolean;
  emissionMaster: Option[];
  isDuplicate: boolean;
  onDuplicateChange: (isDup: boolean) => void;
  onCloseModal: () => void;
  onSave: (mode: "insert" | "update", data: SettingChemicalProp) => void;
}) => {
  const { form, open, mode, loading, emissionMaster, isDuplicate, onDuplicateChange, onCloseModal, onSave } = props;
  const precision = 2;
  const [errorDuplicate, setErrorDuplicate] = useState<string | null>(null);

  const handleSave = async () => {
    try {
      await form.validateFields();
      const formData = form.getFieldsValue();
      const convertData = cleanNumberStringInObject(formData, ["molecularWeight", "density", "temperature", "vaporPressure"]);
      onSave(mode, convertData as unknown as SettingChemicalProp);
    } catch (error) {
      // console.log("validate failed");
      console.error(error);
    }
  };

  const onChangeForm = (changeValue: any) => {
    // Check Duplicate record
    // console.log("Change value : ", changeValue);
    if (["chemicalName", "molecularWeight", "density", "temperature", "vaporPressure"].some((key) => changeValue[key])) {
      onDuplicateChange(false);
    }

    // Check if Key Emission is more than 100.
    if (changeValue.emission) {
      Object.keys(changeValue.emission).forEach((key) => {
        if (changeValue.emission[key] && changeValue.emission[key] > 100) {
          form.setFieldsValue({
            emission: {
              [key]: 100,
            },
          });
        }
      });
    }
  };

  useEffect(() => {
    const errorText = isDuplicate
      ? "Chemical Name, Molecular Weight, Density, Temperature and Vapor Pressure already exists in the system."
      : "";
    setErrorDuplicate(errorText);
    form.setFields(
      ["chemicalName", "molecularWeight", "density", "temperature", "vaporPressure"].map((field) => ({
        name: field,
        errors: isDuplicate ? [""] : [],
      })),
    );
  }, [isDuplicate]);

  return (
    <div>
      <Modal
        centered
        maskClosable={false}
        open={open}
        onCancel={onCloseModal}
        width={1200}
        title={<ModalTitle title="Chemical Property" />}
        footer={
          <Row className="w-full border-t" justify={"end"}>
            <Button className="mt-4" key="submit" type="primary" loading={loading} onClick={handleSave}>
              Save
            </Button>
          </Row>
        }
      >
        {errorDuplicate && (
          <>
            <Row className="w-full" justify={"center"}>
              <Text type="danger">{errorDuplicate}</Text>
            </Row>
          </>
        )}
        <Form size="middle" layout="vertical" autoComplete="off" form={form} requiredMark={false} onValuesChange={onChangeForm}>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Chemical Name"} required />}
                name="chemicalName"
                rules={[{ required: true, message: generateErrorMessage("Chemical Name") }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Molecular Weight"} required />}
                name="molecularWeight"
                rules={[{ required: true, message: generateErrorMessage("Molecular Weight") }]}
              >
                <MoneyInput addonAfter={"lb/in²"} precision={2} formInstance={form} field="molecularWeight" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Density"} required />}
                name="density"
                rules={[{ required: true, message: generateErrorMessage("Density") }]}
              >
                <MoneyInput addonAfter={"lb/gal"} precision={4} formInstance={form} field="density" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Temperature"} required />}
                name="temperature"
                rules={[{ required: true, message: generateErrorMessage("Temperature") }]}
              >
                <MoneyInput addonAfter={"℃"} precision={2} formInstance={form} field="temperature" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Vapor Pressure"} required />}
                name="vaporPressure"
                rules={[{ required: true, message: generateErrorMessage("Vapor Pressure") }]}
              >
                <MoneyInput addonAfter={"lb/in²"} precision={4} formInstance={form} field="vaporPressure" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={12}>
              <Form.Item label={<FormLabel label="Emission" />} style={{ marginBottom: 0 }}>
                <Row gutter={[10, 10]} align="middle">
                  {emissionMaster.map((emission, index) => (
                    <Col key={`emission-${emission}-${index}`} xs={24} sm={8} md={8}>
                      <Row gutter={[16, 16]}>
                        <Col span={6} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", padding: "6px" }}>
                          {emission.label}
                        </Col>
                        <Col span={18}>
                          <Form.Item style={{ marginBottom: 0 }} name={["emission", `${emission.value}`]}>
                            <InputNumber
                              min={0}
                              max={100}
                              disabled={emission.value === "tvoc" ? true : false}
                              key={`emission-${emission}-${index}`}
                              style={{ width: "100%" }}
                              addonAfter={"%"}
                              onKeyDown={(e) => {
                                onKeyDownNumberWithFixedDecimal(e, 2);
                              }}
                              formatter={(value) => formatNumber(value as number, precision)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>
                  ))}
                </Row>
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item label={<FormLabel label={"Remark"} />} name="remark">
                <Input.TextArea rows={3}></Input.TextArea>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};
export default ChemicalPropertyModal;
